import { useEffect, useState } from 'react';
import {
  LIVE_STATS_ENABLED,
  MEMBER_COUNT,
  formatMemberCount,
  parseMemberCount,
} from '../lib/subreddit';
import { fetchSiteSettings } from '../lib/api';

/**
 * Member count for the community-partner widgets, resolved in three tiers:
 *
 *   1. Live Reddit data from /api/subreddit-stats, once LIVE_STATS_ENABLED is on.
 *   2. `membersCount` from the admin dashboard's site settings — the field the
 *      client edits, and the reason "the count doesn't update" was reported.
 *   3. The built-in MEMBER_COUNT constant, so a widget always renders a number
 *      even if both requests fail.
 */

interface SubredditStats {
  /** Raw subscriber count. */
  subscribers: number;
  /** Reddit-style abbreviation, e.g. '9.6k'. */
  members: string;
  /** True only when the count came from the API rather than the constant. */
  isLive: boolean;
}

/**
 * Both widgets can mount in the same tree, and the count is identical for all of
 * them, so the result is memoised at module scope and in-flight requests are
 * shared. One fetch per page load, not one per widget.
 */
let cachedSubscribers: number | null = null;
let inFlight: Promise<number | null> | null = null;

function loadSubscribers(): Promise<number | null> {
  if (cachedSubscribers !== null) return Promise.resolve(cachedSubscribers);
  if (inFlight) return inFlight;

  inFlight = fetch('/api/subreddit-stats')
    .then((response) => (response.ok ? response.json() : null))
    .then((body: { subscribers?: unknown } | null) => {
      const subscribers = body?.subscribers;
      if (typeof subscribers !== 'number' || !Number.isFinite(subscribers)) return null;
      cachedSubscribers = subscribers;
      return subscribers;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

let cachedAdminCount: number | null = null;
let adminInFlight: Promise<number | null> | null = null;

/**
 * The count the client maintains in admin settings ("Members Count Display").
 * It is free text — the field's own help note offers '11.2k', '500' and '1.5M'
 * — so it goes through parseMemberCount rather than Number(). Blank, unparseable
 * and zero values all yield null so the caller falls back instead of rendering
 * nonsense. fetchSiteSettings() is already cached for five minutes and already
 * called on load, so this costs no extra request.
 */
function loadAdminCount(): Promise<number | null> {
  if (cachedAdminCount !== null) return Promise.resolve(cachedAdminCount);
  if (adminInFlight) return adminInFlight;

  adminInFlight = fetchSiteSettings()
    .then((settings) => {
      const parsed = parseMemberCount(String(settings?.membersCount ?? ''));
      if (parsed === null) return null;
      cachedAdminCount = parsed;
      return parsed;
    })
    .catch(() => null)
    .finally(() => {
      adminInFlight = null;
    });

  return adminInFlight;
}

export function useSubredditStats(): SubredditStats {
  const [subscribers, setSubscribers] = useState<number | null>(cachedSubscribers);
  const [adminCount, setAdminCount] = useState<number | null>(cachedAdminCount);

  useEffect(() => {
    let active = true;

    if (LIVE_STATS_ENABLED && subscribers === null) {
      loadSubscribers().then((value) => {
        if (active && value !== null) setSubscribers(value);
      });
    }

    if (adminCount === null) {
      loadAdminCount().then((value) => {
        if (active && value !== null) setAdminCount(value);
      });
    }

    return () => {
      active = false;
    };
    // Runs once per mount; the counts are only read to skip an already-loaded value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolved = subscribers ?? adminCount ?? MEMBER_COUNT;
  return {
    subscribers: resolved,
    members: formatMemberCount(resolved),
    isLive: subscribers !== null,
  };
}
